/**
 * Check-in Permissions & Feature Gating
 * 
 * Defines check-in options available based on:
 * - User role (leader/member)
 * - User level (free/runner/club_leader)
 * - Club subscription (free/club)
 * - Event creator status
 */

import { type Event } from './api/events';
import { UserLevel, SubscriptionLevel } from './api';

export type CheckinOption = 'gps' | 'qr' | 'manual' | 'photo' | 'admin_checkin_others';

export type LockedFeature = 'qr' | 'photo' | 'admin_checkin_others';

export interface CheckinPermissionsInput {
  event: Event;
  userRole: 'leader' | 'member';
  userLevel: UserLevel;
  clubSubscriptionLevel: SubscriptionLevel;
  isEventCreator: boolean;
}

export interface CheckinPermissions {
  enabled: boolean;
  availableOptions: CheckinOption[];
  lockedFeatures: LockedFeature[];
  canCheckinOthers: boolean;
  disabledReason?: string;
}

/**
 * Calculate what check-in options are available for a user
 */
export function calculateCheckinPermissions(input: CheckinPermissionsInput): CheckinPermissions {
  const { userRole, userLevel, clubSubscriptionLevel, isEventCreator } = input;

  // Base permissions - everyone can GPS check-in
  const availableOptions: CheckinOption[] = ['gps'];
  const lockedFeatures: LockedFeature[] = [];

  // QR code check-in: Available to Runner+ users OR Club subscription
  const hasQR = 
    userLevel === UserLevel.RUNNER || 
    userLevel === UserLevel.CLUB_LEADER ||
    clubSubscriptionLevel === SubscriptionLevel.CLUB;
  
  if (hasQR) {
    availableOptions.push('qr');
  } else {
    lockedFeatures.push('qr');
  }

  // Manual check-in: Always available as fallback
  availableOptions.push('manual');

  // Photo check-in: Available to Club Leader users OR Club subscription
  const hasPhoto = 
    userLevel === UserLevel.CLUB_LEADER ||
    clubSubscriptionLevel === SubscriptionLevel.CLUB;
  
  if (hasPhoto) {
    availableOptions.push('photo');
  } else {
    lockedFeatures.push('photo');
  }

  // Admin check-in others: Available to admins and event creators
  const canCheckinOthers = userRole === 'leader' || isEventCreator;
  
  if (canCheckinOthers) {
    availableOptions.push('admin_checkin_others');
  } else {
    lockedFeatures.push('admin_checkin_others');
  }

  return {
    enabled: true,
    availableOptions,
    lockedFeatures,
    canCheckinOthers
  };
}

/**
 * Get UI description for each check-in option
 */
export function getCheckinOptionDescription(option: CheckinOption): {
  title: string;
  description: string;
  icon: string;
} {
  switch (option) {
    case 'gps':
      return {
        title: 'GPS Check-in',
        description: 'Use your location to check in',
        icon: '📍'
      };
    case 'qr':
      return {
        title: 'QR Code',
        description: 'Scan event QR code',
        icon: '📱'
      };
    case 'manual':
      return {
        title: 'Manual',
        description: 'Check in without location',
        icon: '✋'
      };
    case 'photo':
      return {
        title: 'Photo Check-in',
        description: 'Take a photo to check in',
        icon: '📸'
      };
    case 'admin_checkin_others':
      return {
        title: 'Check In Others',
        description: 'Admin check-in for attendees',
        icon: '👥'
      };
  }
}

/**
 * Get upgrade message for locked features
 */
export function getUpgradeMessage(lockedFeatures: LockedFeature[]): string {
  if (lockedFeatures.length === 0) return '';

  const featureNames = lockedFeatures.map(f => {
    switch (f) {
      case 'qr': return 'QR code check-in';
      case 'photo': return 'Photo check-in';
      case 'admin_checkin_others': return 'Admin check-in';
    }
  });

  if (featureNames.length === 1) {
    return `Upgrade to Runner to unlock ${featureNames[0]}`;
  }

  return `Upgrade to Club Leader to unlock all check-in features`;
}
